import {pipe} from "mu-lambda";

import {Errors, KAError} from "@khan/logging";

import {type MakeUrlFn, type MapIdToPathFn, type UrlRule} from "./types.ts";

let mapIdToPath: MapIdToPathFn = () => null;
let urlConstructionRules: Array<UrlRule> = [];

/**
 * Configure the URL construction rules.
 *
 * These rules are used by the `urlBuildFactory` method to process
 * URL construction.
 *
 * @param idToPathMapper A function that maps an ID to a path. This is used to
 * determine the base path for the URL. The function should return a string
 * that is the path for the given ID. If the ID is not configured for the app,
 * the function should return nullish. The function should not throw an error.
 * @param rules The array of rules to be applied in the order that they are
 * to be applied.
 */
export function configureUrlConstructionRules(
    idToPathMapper: MapIdToPathFn,
    rules: ReadonlyArray<UrlRule>,
): void {
    mapIdToPath = idToPathMapper;
    urlConstructionRules = [...rules];
}

const getConstructionRules = (): ReadonlyArray<UrlRule> => {
    return urlConstructionRules;
};

export function getMapIdToPath(): MapIdToPathFn {
    return mapIdToPath;
}

/**
 * Build a function for creating URLs to the given base path.
 *
 * Returns a function that takes query params and creates a new URL
 * for the specified base path and params. The base path and params are passed
 * to configured rules to determine the appropriate URL to generate. Each rule
 * can modify the base path and also any query params, including the
 * `continue` param.
 *
 * This allows certain paths to be specially handled.
 *
 * @param id The id of the URL path to be modified by the built function.
 * @return A function that takes a URLSearchParams instance and
 * returns the actual URL path that should be used given the current location.
 */
export function urlBuilderFactory(pathId: string): MakeUrlFn {
    return (
        paramsOrContinueUrl?:
            | Nullable<string>
            | URLSearchParams
            | Record<string, string>,
    ): string => {
        // If we're running tests, we fallback to a basic mapping.
        const basePath = mapIdToPath(pathId);
        if (basePath == null) {
            throw new KAError(
                "No path configured for the given ID",
                Errors.InvalidInput,
                {
                    metadata: {
                        pathId,
                    },
                },
            );
        }

        const url = new URL(basePath, location.origin);
        const composedRules = pipe(...getConstructionRules());
        const {basePath: finalBasesPath, queryParams} = composedRules({
            // The base path is the the pathname, or origin and pathname
            // if the origin is different from the current location.
            // We do not want the params here or the hash.
            basePath:
                url.origin === location.origin
                    ? url.pathname
                    : `${url.origin}${url.pathname}`,
            queryParams:
                typeof paramsOrContinueUrl === "string"
                    ? new URLSearchParams({continue: paramsOrContinueUrl})
                    : paramsOrContinueUrl instanceof URLSearchParams
                      ? paramsOrContinueUrl
                      : paramsOrContinueUrl == null
                        ? new URLSearchParams()
                        : new URLSearchParams(paramsOrContinueUrl),
        });

        // We have a result.
        const queryString =
            queryParams.size > 0 ? `?${queryParams.toString()}` : "";
        return `${finalBasesPath}${queryString}`;
    };
}
